import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { __ } from "@common/helpers/i18n";
import { icon } from "@common/helpers/icons";
import { ConfirmProps } from "@common/types";
import { FooterActions } from "../container";
import { Button } from "../form";
import { Modal } from "./Modal";

const typeBackground = {
  danger: "bg-destructive/10",
  warning: "bg-warning/10",
  info: "bg-info/10",
  success: "bg-success/10",
};

const typeIcon = {
  danger: icon("alert-triangle", { className: "h-6 w-6 text-destructive" }),
  warning: icon("alert-triangle", { className: "h-6 w-6 text-warning" }),
  info: icon("alert-triangle", { className: "h-6 w-6 text-info" }),
  success: icon("alert-triangle", { className: "h-6 w-6 text-success" }),
};

function ConfirmModal({
  title,
  caption,
  okText,
  cancelText,
  type = "danger",
  icon,
  okDelaySeconds = 0,
  onCancel,
  onOk,
  onClose,
}: {
  onClose: () => void;
} & ConfirmProps) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const delay = Math.max(0, Math.floor(okDelaySeconds));
  const [remainingSeconds, setRemainingSeconds] = useState(delay);
  const canConfirm = remainingSeconds <= 0;

  useEffect(() => {
    if (delay <= 0) {
      return;
    }

    setRemainingSeconds(delay);
    const timer = window.setInterval(() => {
      setRemainingSeconds((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          return 0;
        }

        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [delay]);

  const confirmLabel = okText ? okText : __("Ok");

  return (
    <Modal
      onClose={onClose}
      isOpen
      initialFocus={cancelButtonRef}
      padding="none"
    >
      <Modal.Body>
        <div className="sm:flex sm:items-start">
          <div
            className={classNames(
              "mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10",
              typeBackground[type],
            )}
          >
            {icon || typeIcon[type]}
          </div>
          <div className="mt-3 text-center sm:ms-4 sm:mt-0 sm:text-start">
            {title && (
              <Modal.Title
                as="h3"
                className="text-base font-semibold leading-6 text-foreground"
              >
                {title}
              </Modal.Title>
            )}
            <div className="mt-2">
              <p>{caption}</p>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <FooterActions>
          <Button
            variant="outline-light"
            onClick={() => {
              onCancel?.();
              onClose();
            }}
            ref={cancelButtonRef}
          >
            {cancelText ? cancelText : __("Cancel")}
          </Button>
          <Button
            variant={type}
            disabled={!canConfirm}
            onClick={() => {
              if (!canConfirm) {
                return;
              }

              onOk();
              onClose();
            }}
          >
            {canConfirm
              ? confirmLabel
              : `${confirmLabel} (${remainingSeconds})`}
          </Button>
        </FooterActions>
      </Modal.Footer>
    </Modal>
  );
}

export function Confirm(props: ConfirmProps) {
  let divTarget = document.getElementById("confirm-modal");
  if (!divTarget) {
    divTarget = document.createElement("div");
    divTarget.id = "confirm-modal";
    document.body.appendChild(divTarget);
  }

  const root = createRoot(divTarget);

  function removeElementReconfirm() {
    const target = document.getElementById("confirm-modal");
    if (target) {
      root.unmount();
      target.parentNode!.removeChild(target);
    }
  }
  divTarget.focus();

  setTimeout(() => {
    root.render(
      <ConfirmModal
        {...props}
        onClose={() => {
          removeElementReconfirm();
        }}
      />,
    );
  }, 50);
}
